// MemberManagement.jsx
import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Divider, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Tooltip, Chip, TablePagination, Alert, FormHelperText
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import BlockIcon from "@mui/icons-material/Block";
import LockResetIcon from "@mui/icons-material/LockReset";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";
import { motion } from "framer-motion";
import { fetchMembers, createMember, updateMember, resetMemberPassword } from "../services/memberService";
import LoadingSpinner from "../components/LoadingSpinner";
import { formatNidaId, formatVoterId, formatPhoneNumber, isValidPhoneNumber } from "../shared/formatter";
import PhoneInput from "../components/PhoneInput";
import { useTranslation } from "react-i18next";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.4 },
  }),
};

const MemberManagement = () => {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogError, setDialogError] = useState("");
  const [query, setQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [editingMember, setEditingMember] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    role: "Normal Member",
    number_of_slots: 1,
    nmb_account_number: "",
    identity_type: "NIDA",
    identity_number: "",
    heir: { full_name: "", relationship: "", contact: "", nmb_account_number: "" },
    is_active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const leadershipRoles = useMemo(() => ["Mwenyekiti", "Katibu", "Muhasibu"], []);
  const leadershipHolders = useMemo(() => {
    const active = members.filter((m) => m.is_active);
    return leadershipRoles.reduce((acc, role) => {
      const member = active.find((m) => m.role === role);
      if (member) acc[role] = member;
      return acc;
    }, {});
  }, [members, leadershipRoles]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchMembers();
      setMembers(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      Swal.fire({
        icon: "error",
        title: t("Failed to load members"),
        text: err.message,
        confirmButtonColor: brand,
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter((m) => {
      const values = [
        m.name,
        m.email,
        m.role,
        m.user?.full_name,
        m.user?.email,
        m.user?.phone_number,
      ].filter(Boolean);
      return values.some((v) => v.toLowerCase().includes(q));
    });
  }, [query, members]);

  const paginated = useMemo(() => {
    if (rowsPerPage === -1) return filtered;
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    const val = parseInt(e.target.value, 10);
    setRowsPerPage(val);
    setPage(0);
  };

  const handleOpenAdd = () => {
    setDialogMode("add");
    setEditingMember(null);
    setFormErrors({});
    setForm({
      name: "",
      email: "",
      phone: "",
      role: "Normal Member",
      number_of_slots: 1,
      nmb_account_number: "",
      identity_type: "NIDA",
      identity_number: "",
      heir: { full_name: "", relationship: "", contact: "", nmb_account_number: "" },
      is_active: true,
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (member) => {
    setDialogMode("edit");
    setEditingMember(member);
    setFormErrors({});
    setForm({
      name: member.user.full_name,
      email: member.user.email,
      phone: member.user.phone_number,
      role: member.role,
      number_of_slots: member.number_of_slots || 1,
      nmb_account_number: member.nmb_account_number || "",
      identity_type: member.identity_type || "NIDA",
      identity_number:
        (member.identity_type || "NIDA") === "VOTER"
          ? formatVoterId(member.identity_number || "")
          : formatNidaId(member.identity_number || ""),
      heir: {
        ...(member.heir || { full_name: "", relationship: "", contact: "", nmb_account_number: "" }),
        nmb_account_number: member.heir?.nmb_account_number || "",
      },
      is_active: member.is_active,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormErrors({});
    setDialogError("");
  };

  const handleSave = async () => {
    const nextErrors = {};
    const nmbRegex = /^\d{11}$/;
    const nidaRegex = /^\d{20}$/;
    const voterRegex = /^T-\d{4}-\d{4}-\d{3}-\d$/;
    if (!form.name?.trim()) nextErrors.name = t("Full name is required.");
    if (!form.phone?.trim()) {
      nextErrors.phone = t("Phone number is required.");
    } else {
      if (!isValidPhoneNumber(form.phone)) {
        nextErrors.phone = t("Phone number must follow the format 255xxxxxxxxx");
      }
    }
    if (!form.nmb_account_number?.trim() || !nmbRegex.test(form.nmb_account_number.trim())) {
      nextErrors.nmb_account_number = t("NMB account must be exactly 11 digits.");
    }
    if (!form.identity_type) {
      nextErrors.identity_type = t("Please select an identity type.");
    }
    const identityValue = form.identity_number?.trim() || "";
    if (!identityValue) {
      nextErrors.identity_number = t("Identity number is required.");
    } else if (form.identity_type === "NIDA") {
      const digitsOnly = identityValue.replace(/\D/g, "");
      if (!nidaRegex.test(digitsOnly)) {
        nextErrors.identity_number = t("NIDA ID must be 20 digits.");
      }
    } else if (form.identity_type === "VOTER") {
      if (!voterRegex.test(identityValue.toUpperCase())) {
        nextErrors.identity_number = t("Voter ID must match T-xxxx-xxxx-xxx-x format.");
      }
    }
    const heir = form.heir || {};
    const hasHeirInput = [
      heir.full_name,
      heir.relationship,
      heir.contact,
      heir.nmb_account_number,
    ].some((v) => (v || "").trim() !== "");
    if (hasHeirInput) {
      if (!heir.full_name?.trim()) nextErrors.heir_full_name = t("Heir name is required.");
      if (!heir.relationship?.trim()) nextErrors.heir_relationship = t("Relationship is required.");
      if (!heir.contact?.trim()) {
        nextErrors.heir_contact = t("Heir contact is required.");
      } else if (!isValidPhoneNumber(heir.contact)) {
        nextErrors.heir_contact = t("Heir contact must follow the format 255xxxxxxxxx");
      }
      const heirAcc = heir.nmb_account_number?.trim() || "";
      if (!heirAcc || !nmbRegex.test(heirAcc)) {
        nextErrors.heir_nmb_account_number = t("Heir NMB account must be exactly 11 digits.");
      }
    }
    if (!form.number_of_slots || form.number_of_slots < 1) nextErrors.number_of_slots = t("Slots must be at least 1.");
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (dialogMode === "add") {
      try {
        await createMember(form);
        setDialogError("");
        Swal.fire({
          icon: "success",
          title: t("Member Added!"),
          text: t("{{name}} has been added to the group.", { name: form.name }),
          confirmButtonColor: brand,
        });
        loadData();
        handleCloseDialog();
      } catch (err) {
        setDialogError(err.message || t("Creation failed. Please review the errors and try again."));
      }
    } else {
      const payload = {
        user: {
          full_name: form.name,
          email: form.email,
          phone_number: formatPhoneNumber(form.phone),
        },
        role: form.role,
        number_of_slots: form.number_of_slots,
        heir_data: form.heir,
        is_active: form.is_active,
        nmb_account_number: form.nmb_account_number,
        identity_type: form.identity_type,
        identity_number: form.identity_number,
      };
      try {
        await updateMember(editingMember.id, payload);
        setDialogError("");
        Swal.fire({ icon: "success", title: t("Member Updated!"), confirmButtonColor: brand });
        loadData();
        handleCloseDialog();
      } catch (err) {
        setDialogError(err.message || t("Update failed. Please review the errors and try again."));
      }
    }
  };

  const handleDelete = (member) => {
    Swal.fire({
      title: t("Remove Member?"),
      text: t("Are you sure you want to remove {{name}}?", { name: member.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: brand,
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("Delete"),
    }).then((res) => {
      if (res.isConfirmed) {
        setMembers((prev) => prev.filter((m) => m.id !== member.id));
        Swal.fire({
          icon: "success",
          title: t("Deleted"),
          text: t("{{name}} removed successfully.", { name: member.name }),
          confirmButtonColor: brand,
        });
      }
    });
  };

  const handleResetPassword = (member) => {
    const defaultPasswordFormat = `${member.user.full_name.substring(0, 3).toLowerCase()}${member.user.phone_number.slice(-4)}!`;

    const htmlContent = `
      <div style="text-align: left;">
        <p>${t("Are you sure you want to reset the password for {{name}}?", {
      name: `<strong>${member.user.full_name}</strong>`,
    })}</p>
        <p>${t("The password will be set to a default value based on their name and phone number.")}</p>
        <p>${t("New Password: {{password}}", { password: `<strong>${defaultPasswordFormat}</strong>` })}</p>
        <p>${t("The member will be required to change this password on their next login.")}</p>
      </div>
    `;

    Swal.fire({
      title: t("Reset Password?"),
      html: htmlContent,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: brand,
      confirmButtonText: t("Yes, Reset It!"),
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await resetMemberPassword(member.id);
        Swal.fire(t("Success!"), t("The member's password has been reset."), "success");
      } catch (err) {
        Swal.fire(t("Failed!"), err.message || t("Could not reset the password."), "error");
      }
    });
  };


  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 900, color: brand }}>
        {t("Member Management")}
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        {t("Assign members and register heirs for your group.")}
      </Typography>

      {/* Toolbar */}
      <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder={t("Search by name, email, or role...")}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAdd}
                sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
              >
                {t("Add Member")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Typography color="error" align="center" sx={{ my: 4 }}>
          {t("Failed to load members: {{message}}", { message: error })}
        </Typography>
      )}

      {/* Members Table */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Name")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Role")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Phone")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="center">
                      {t("Slots")}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Payment Ref")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Heir")}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t("Status")}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {t("Actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 && !loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        {t("No members found.")}
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((m, idx) => (
                      <TableRow key={m.id} hover>
                        <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>{m.user.full_name}</TableCell>
                        <TableCell>{m.role}</TableCell>
                        <TableCell>{m.user.phone_number}</TableCell>
                        <TableCell align="center">{m.number_of_slots || 1}</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{m.payment_reference}</TableCell>
                        <TableCell>
                          {m.heir?.full_name} ({m.heir?.relationship})
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={m?.is_active ? t("Active") : t("Inactive")}
                            color={m.is_active ? "success" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={t("Edit")}>
                            <IconButton onClick={() => handleOpenEdit(m)} sx={{ mr: 0.5 }}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t("Reset Password")}>
                            <IconButton onClick={() => handleResetPassword(m)} color="primary" sx={{ mr: 0.5 }}>
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>

                          {/* <Tooltip title="Delete">
    <IconButton onClick={() => handleDelete(m)} color="error">
      <DeleteIcon />
    </IconButton>
  </Tooltip> */}
                        </TableCell>

                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2 }}>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage === -1 ? filtered.length : rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100, { label: t("All"), value: -1 }]}
              />
            </Box>
          </CardContent>
        </Card>
      </motion.div>

      {/* Add/Edit Member Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          {dialogMode === "add" ? t("Add New Member") : t("Edit Member")}
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: brand }}>
            {t("Member Information")}
          </Typography>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Full Name")}
                fullWidth
                value={form.name}
                error={Boolean(formErrors.name)}
                helperText={formErrors.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} // This will be part of the user object on save
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Email")}
                fullWidth
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} // This will be part of the user object on save
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <PhoneInput
                label={t("Phone Number")}
                fullWidth
                value={form.phone}
                placeholder={t("e.g. 712345678")}
                error={Boolean(formErrors.phone)}
                helperText={formErrors.phone}
                onChange={(val) => setForm((f) => ({ ...f, phone: val }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Member NMB Account Number")}
                fullWidth
                value={form.nmb_account_number}
                error={Boolean(formErrors.nmb_account_number)}
                helperText={formErrors.nmb_account_number || t("Must be exactly 11 digits.")}
                inputProps={{ inputMode: "numeric", pattern: "\\d*", maxLength: 11 }}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setForm((f) => ({ ...f, nmb_account_number: digits }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Role")}</InputLabel>
                <Select
                  label={t("Role")}
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                >
                  {["Mwenyekiti", "Katibu", "Muhasibu"].map((role) => {
                    const holder = leadershipHolders[role];
                    const isEditingSameRole =
                      editingMember && editingMember.role === role && editingMember.is_active;
                    const disabled = Boolean(holder && !isEditingSameRole);
                    return (
                      <MenuItem key={role} value={role} disabled={disabled}>
                        {t(role)}
                        {disabled ? ` (${t("Assigned")})` : ""}
                      </MenuItem>
                    );
                  })}
                  <MenuItem value="Normal Member">{t("Normal Member")}</MenuItem>
                </Select>
                <FormHelperText>
                  {t("Leadership roles are limited to one active member per group.")}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={Boolean(formErrors.identity_type)}>
                <InputLabel>{t("Identity Type")}</InputLabel>
                <Select
                  label={t("Identity Type")}
                  value={form.identity_type}
                  onChange={(e) =>
                    setForm((prev) => {
                      const value = e.target.value;
                      return {
                        ...prev,
                        identity_type: value,
                        identity_number:
                          value === "VOTER"
                            ? formatVoterId((prev.identity_number || "").replace(/[^0-9T]/gi, ""))
                            : formatNidaId((prev.identity_number || "").replace(/\D/g, "")),
                      };
                    })
                  }
                >
                  <MenuItem value="NIDA">{t("NIDA ID")}</MenuItem>
                  <MenuItem value="VOTER">{t("Voter ID")}</MenuItem>
                </Select>
                {formErrors.identity_type && (
                  <Typography variant="caption" color="error">
                    {formErrors.identity_type}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Identity Number")}
                fullWidth
                value={form.identity_number}
                error={Boolean(formErrors.identity_number)}
                helperText={
                  form.identity_type === "VOTER"
                    ? formErrors.identity_number || t("Format: T-1234-5678-901-2")
                    : formErrors.identity_number || t("NIDA IDs must have 20 digits.")
                }
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    identity_number:
                      prev.identity_type === "VOTER"
                        ? formatVoterId(e.target.value.toUpperCase())
                        : formatNidaId(e.target.value),
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Number of Slots")}
                type="number"
                fullWidth
                value={form.number_of_slots}
                inputProps={{ min: 1 }}
                error={Boolean(formErrors.number_of_slots)}
                helperText={
                  formErrors.number_of_slots ||
                  t("Each slot represents a household/person you contribute for.")
                }
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    number_of_slots: Number(e.target.value),
                  }))
                }
              />
            </Grid>
            {dialogMode === 'edit' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t("Status")}</InputLabel>
                  <Select
                    label={t("Status")}
                    value={form.is_active}
                    onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value }))}
                  >
                    <MenuItem value={true}>{t("Active")}</MenuItem>
                    <MenuItem value={false}>{t("Inactive")}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: brand }}>
            {t("Mrithi (Heir) Details — optional")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Heir Full Name")}
                fullWidth
                value={form.heir.full_name}
                error={Boolean(formErrors.heir_full_name)}
                helperText={formErrors.heir_full_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, heir: { ...f.heir, full_name: e.target.value } }))
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Relationship")}
                fullWidth
                value={form.heir.relationship}
                error={Boolean(formErrors.heir_relationship)}
                helperText={formErrors.heir_relationship}
                onChange={(e) =>
                  setForm((f) => ({ ...f, heir: { ...f.heir, relationship: e.target.value } }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <PhoneInput
                label={t("Heir Contact")}
                fullWidth
                value={form.heir.contact}
                error={Boolean(formErrors.heir_contact)}
                helperText={formErrors.heir_contact}
                onChange={(val) =>
                  setForm((f) => ({ ...f, heir: { ...f.heir, contact: val } }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("Heir NMB Account Number")}
                fullWidth
                value={form.heir.nmb_account_number}
                error={Boolean(formErrors.heir_nmb_account_number)}
                helperText={formErrors.heir_nmb_account_number || t("Must be exactly 11 digits.")}
                inputProps={{ inputMode: "numeric", pattern: "\\d*", maxLength: 11 }}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
                  setForm((f) => ({ ...f, heir: { ...f.heir, nmb_account_number: digits } }));
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ bgcolor: brand, "&:hover": { bgcolor: brandHover } }}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default MemberManagement;
